import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Server, HardDrive, Gamepad2, Globe, Check, ArrowRight, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PRODUCTS = [
  {
    icon: Gamepad2,
    name: 'Game Server',
    tag: 'Popular',
    price: 'from €1.00/mo.',
    href: '/gameservers',
    color: 'text-green-400',
    glow: 'rgba(74,222,128,0.12)',
    specs: [
      'CS2, Minecraft, ARK & 100+ games',
      'High-clock AMD Ryzen CPUs',
      'Instant setup via web interface',
      'DDoS protection included',
      'Prepaid – no contract',
    ],
  },
  {
    icon: Server,
    name: 'KVM VPS',
    tag: 'Recommended',
    price: 'from €2.35/mo.',
    href: '/vps',
    color: 'text-primary',
    glow: 'rgba(255,107,0,0.14)',
    featured: true,
    specs: [
      'AMD EPYC / Ryzen NVMe',
      'Full root access (KVM)',
      'Snapshots & backups',
      '10 Gbit/s uplink',
      'DDoS protection included',
    ],
  },
  {
    icon: HardDrive,
    name: 'Root Server',
    tag: 'Performance',
    price: 'from €9.99/mo.',
    href: '/rootserver',
    color: 'text-blue-400',
    glow: 'rgba(96,165,250,0.12)',
    specs: [
      'AMD EPYC / Intel Xeon',
      'Full root access (KVM)',
      'NVMe RAID storage',
      '10 Gbit/s uplink',
      'DDoS protection included',
    ],
  },
];

export function ProductsShowcase() {
  return (
    <section
      className="section-spacing relative overflow-hidden"
      data-testid="products-showcase-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/3 to-transparent pointer-events-none" />

      <div className="container-main relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">Our Products</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <h2 className="text-4xl md:text-5xl font-black leading-tight">
              The perfect server<br />
              <span className="text-gradient">for every need</span>
            </h2>
            <Link to="/gameservers" className="shrink-0">
              <Button variant="outline" className="border-primary/30 hover:border-primary font-bold">
                All Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRODUCTS.map((product, i) => (
            <motion.div
              key={product.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              data-testid={`product-card-${i}`}
              className={`relative group flex flex-col rounded-2xl border overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${
                product.featured
                  ? 'border-primary/40 bg-card'
                  : 'border-border bg-card'
              }`}
              style={{
                boxShadow: product.featured
                  ? `0 0 40px ${product.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`
                  : `0 0 0px transparent`,
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 40px ${product.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = product.featured ? `0 0 40px ${product.glow}, inset 0 1px 0 rgba(255,255,255,0.05)` : `0 0 0px transparent`; }}
            >
              {/* Top accent bar */}
              <div className={`h-0.5 w-full ${product.featured ? 'bg-primary' : `bg-gradient-to-r from-transparent via-white/10 to-transparent`}`} />

              {product.featured && (
                <div className="absolute top-4 right-4 text-xs font-bold px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/30">
                  {product.tag}
                </div>
              )}

              <div className="p-7 flex flex-col flex-1">
                {/* Icon + Name */}
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-white/5 border border-white/8`}>
                    <product.icon className={`w-5 h-5 ${product.color}`} />
                  </div>
                  <div>
                    <div className="font-black text-lg leading-tight">{product.name}</div>
                    {!product.featured && (
                      <div className={`text-xs font-bold ${product.color}`}>{product.tag}</div>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="mb-5">
                  <div className={`text-3xl font-black ${product.color}`}>{product.price}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">incl. VAT · Prepaid</div>
                </div>

                <div className="h-px bg-border mb-5" />

                {/* Specs */}
                <ul className="space-y-2.5 flex-1 mb-7">
                  {product.specs.map(spec => (
                    <li key={spec} className="flex items-start gap-2.5 text-sm">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${product.color}`} />
                      <span className="text-muted-foreground leading-snug">{spec}</span>
                    </li>
                  ))}
                </ul>

                <Link to={product.href}>
                  <Button
                    className={`w-full font-bold ${
                      product.featured
                        ? 'bg-primary hover:bg-primary/90 text-white glow-hover'
                        : 'bg-white/5 hover:bg-white/10 border border-white/10 text-foreground'
                    }`}
                    data-testid={`product-card-cta-${i}`}
                  >
                    Configure now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom strip: more products */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35 }}
          className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          {[
            { icon: Globe,      name: 'Web Hosting',  price: 'from €2.99', href: '/webhosting'  },
            { icon: Headphones, name: 'TeamSpeak 3',  price: 'from €1.90', href: '/teamspeak'   },
            { icon: Server,     name: 'Domains',      price: 'from €0.99', href: '/domains'     },
            { icon: HardDrive,  name: 'Dedicated',    price: 'from €49.00', href: '/dedicated'  },
          ].map((item, i) => (
            <Link
              key={item.name}
              to={item.href}
              data-testid={`product-secondary-${i}`}
              className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-muted/60 transition-colors duration-200 group"
            >
              <item.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-bold truncate">{item.name}</div>
                <div className="text-xs text-muted-foreground">{item.price}/Mo.</div>
              </div>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default ProductsShowcase;
